import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { CheckCircle2, XCircle, ClipboardList, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CustomerApprovals() {
  const { user } = useAuth();
  const canApprove = ['Super Admin', 'Admin', 'Branch Manager'].includes(user?.role);
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approveDialog, setApproveDialog] = useState({ open: false, customer: null, ids: [] });
  const [rejectDialog, setRejectDialog] = useState({ open: false, customer: null, reason: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [p, u] = await Promise.all([
        axios.get(`${API_URL}/api/customers/pending/list`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users`, { withCredentials: true }),
      ]);
      setPending(p.data || []);
      setUsers((u.data || []).filter(x => ['Sales Executive', 'Recovery Officer'].includes(x.role)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load pending customers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (canApprove) load(); }, [canApprove]);

  if (!canApprove) {
    return (
      <div className="text-slate-400 py-8 text-center" data-testid="approvals-denied">
        Only Admin, Super Admin and Branch Manager can access customer approvals.
      </div>
    );
  }

  const assignableUsers = (branchId) => users.filter(u => !branchId || u.branch_id === branchId);

  const handleApprove = async () => {
    try {
      const { customer, ids } = approveDialog;
      await axios.post(`${API_URL}/api/customers/${customer.id}/approve`,
        { assigned_user_ids: ids }, { withCredentials: true });
      toast.success(`Approved ${customer.name}` + (ids.length ? ` · assigned to ${ids.length} user(s)` : ''));
      setApproveDialog({ open: false, customer: null, ids: [] });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Approval failed');
    }
  };

  const handleReject = async () => {
    try {
      const { customer, reason } = rejectDialog;
      if (!reason.trim()) { toast.error('Reason is required'); return; }
      await axios.post(`${API_URL}/api/customers/${customer.id}/reject`,
        { reason: reason.trim() }, { withCredentials: true });
      toast.success(`Rejected ${customer.name}`);
      setRejectDialog({ open: false, customer: null, reason: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Rejection failed');
    }
  };

  const toggleAssignee = (uid) => {
    setApproveDialog((s) => {
      const has = s.ids.includes(uid);
      return { ...s, ids: has ? s.ids.filter(x => x !== uid) : [...s.ids, uid] };
    });
  };

  return (
    <div className="space-y-6" data-testid="customer-approvals">
      <div>
        <h1 className="text-3xl font-bold text-white mb-1" data-testid="approvals-title">Customer Approvals</h1>
        <p className="text-slate-400 text-sm">Review customers submitted by branch users. Approve & assign a Sales Executive / Recovery Officer, or reject with a reason.</p>
      </div>

      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white text-base flex items-center">
            <ClipboardList className="h-4 w-4 mr-2 text-amber-500" /> Pending ({pending.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-slate-400 py-8 text-center">Loading…</div>
          ) : pending.length === 0 ? (
            <div className="text-slate-500 py-8 text-center" data-testid="approvals-empty">No customers awaiting approval.</div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-800">
                    <TableHead className="text-slate-400">Customer</TableHead>
                    <TableHead className="text-slate-400">Account #</TableHead>
                    <TableHead className="text-slate-400">CNIC</TableHead>
                    <TableHead className="text-slate-400">Mobile</TableHead>
                    <TableHead className="text-slate-400">Branch</TableHead>
                    <TableHead className="text-slate-400">Sales Executive</TableHead>
                    <TableHead className="text-slate-400">Submitted by</TableHead>
                    <TableHead className="text-slate-400">Submitted</TableHead>
                    <TableHead className="text-slate-400 text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pending.map((c) => (
                    <TableRow key={c.id} className="border-slate-800 hover:bg-slate-800/40" data-testid={`pending-row-${c.id}`}>
                      <TableCell className="text-white">{c.name}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{c.account_number}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{c.cnic}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{c.mobile}</TableCell>
                      <TableCell className="text-slate-300 text-xs">{c.branch_id ? c.branch_id.slice(-6) : '—'}</TableCell>
                      <TableCell className="text-amber-400 text-xs font-medium" data-testid={`sales-exec-${c.id}`}>
                        {c.sales_executive_name
                          ? <span className="font-semibold">{c.sales_executive_name}</span>
                          : <span className="text-slate-600">—</span>}
                      </TableCell>
                      <TableCell className="text-emerald-400 text-xs font-medium" data-testid={`submitted-by-${c.id}`}>
                        {c.submitted_by_name
                          ? <span className="font-semibold">{c.submitted_by_name}</span>
                          : <span className="text-slate-600">—</span>}
                        {c.submitted_by_role && <div className="text-slate-500 text-[10px]">{c.submitted_by_role}</div>}
                      </TableCell>
                      <TableCell className="text-slate-300 text-xs">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                      </TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" data-testid={`approve-${c.id}`}
                          className="bg-emerald-600 hover:bg-emerald-700 h-7 px-2 text-xs"
                          onClick={() => setApproveDialog({ open: true, customer: c, ids: c.submitted_by_id ? [c.submitted_by_id] : [] })}>
                          <CheckCircle2 className="h-3 w-3 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive" data-testid={`reject-${c.id}`}
                          className="h-7 px-2 text-xs"
                          onClick={() => setRejectDialog({ open: true, customer: c, reason: '' })}>
                          <XCircle className="h-3 w-3 mr-1" /> Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approve + optional assign */}
      <Dialog open={approveDialog.open} onOpenChange={(o) => !o && setApproveDialog({ open: false, customer: null, ids: [] })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center"><UserPlus className="h-4 w-4 mr-2 text-emerald-400" /> Approve & Assign</DialogTitle>
          </DialogHeader>
          {approveDialog.customer && (
            <div className="space-y-3">
              <div className="text-sm text-slate-300">
                Approving <b className="text-white">{approveDialog.customer.name}</b> · {approveDialog.customer.account_number}
              </div>
              {(approveDialog.customer.submitted_by_name || approveDialog.customer.submitted_by_employee_id) && (
                <div className="bg-emerald-950/40 border border-emerald-500/30 rounded-sm px-3 py-2 text-xs" data-testid="approve-dialog-submitter">
                  <span className="text-slate-400">Submitted by:</span>{' '}
                  {approveDialog.customer.submitted_by_name && (
                    <span className="text-emerald-400 font-semibold">{approveDialog.customer.submitted_by_name}</span>
                  )}
                  {approveDialog.customer.submitted_by_employee_id && (
                    <span className="text-slate-400 font-mono ml-1">({approveDialog.customer.submitted_by_employee_id})</span>
                  )}
                  {approveDialog.customer.submitted_by_role && (
                    <span className="text-slate-500"> · {approveDialog.customer.submitted_by_role}</span>
                  )}
                  <span className="text-slate-500"> — auto-selected below. Uncheck to change.</span>
                </div>
              )}
              <div>
                <Label className="text-slate-400 text-xs">Assign to (optional — you can also assign later)</Label>
                <div className="max-h-56 overflow-y-auto bg-slate-950 border border-slate-800 rounded-sm p-2 space-y-1 mt-1">
                  {assignableUsers(approveDialog.customer.branch_id).length === 0 && (
                    <div className="text-slate-500 text-xs">No Sales/Recovery users in this branch.</div>
                  )}
                  {assignableUsers(approveDialog.customer.branch_id).map((u) => (
                    <label key={u.id} className="flex items-center gap-2 py-1 cursor-pointer text-sm text-slate-200 hover:text-white"
                      data-testid={`assign-check-${u.id}`}>
                      <input type="checkbox" checked={approveDialog.ids.includes(u.id)}
                        onChange={() => toggleAssignee(u.id)} className="accent-emerald-500" />
                      <span className="font-medium">{u.name || u.username}</span>
                      <span className="text-slate-500 text-xs">({u.role})</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproveDialog({ open: false, customer: null, ids: [] })}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={handleApprove} data-testid="confirm-approve">
              Approve{approveDialog.ids.length ? ` & Assign (${approveDialog.ids.length})` : ''}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject with reason */}
      <Dialog open={rejectDialog.open} onOpenChange={(o) => !o && setRejectDialog({ open: false, customer: null, reason: '' })}>
        <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center text-red-400"><XCircle className="h-4 w-4 mr-2" /> Reject Customer</DialogTitle>
          </DialogHeader>
          {rejectDialog.customer && (
            <div className="space-y-3">
              <div className="text-sm text-slate-300">
                Rejecting <b className="text-white">{rejectDialog.customer.name}</b> · {rejectDialog.customer.account_number}
              </div>
              <div>
                <Label className="text-slate-400 text-xs">Reason (required)</Label>
                <Input value={rejectDialog.reason} onChange={(e) => setRejectDialog((s) => ({ ...s, reason: e.target.value }))}
                  className="bg-slate-800 border-slate-700 text-white mt-1"
                  data-testid="reject-reason" placeholder="e.g. Duplicate CNIC" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectDialog({ open: false, customer: null, reason: '' })}>Cancel</Button>
            <Button variant="destructive" onClick={handleReject} data-testid="confirm-reject">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
